<template>
  <div class="accessory-quantity-selector">
    <b-form-select
      v-if="!quantity.customMode"
      :value="selectedDropdownValue"
      :options="dropdownOptions"
      size="sm"
      @change="setQuantityFromSelect"
    />

    <b-input
      v-else
      ref="quantityControl"
      v-model="quantity.value"
      size="sm"
      @blur="toggleQuantityCustomMode(false)"
    />
  </div>
</template>

<script>
import { COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS, COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS } from '@/constants'
import { closest } from '@/assets/js/helper/array'

const CUSTOM_OPTION_VALUE = '__custom_quantity__'

export default {
  name: 'AccessoryQuantitySelector',
  props: {
    value: {
      type: Number,
      default: 1
    },
    quantityinfo: {
      type: Object,
      default: () => ({})
    },
    showCustomQuantity: {
      type: Boolean,
      default: true
    },
    translations: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      quantity: {
        customMode: false,
        value: this.value,
        tempValue: null
      }
    }
  },
  computed: {
    quantityOptions() {
      const max = Math.trunc(Math.max(1, Number(this.quantityinfo.max || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.max)))
      const min = Math.trunc(Math.max(1, Math.min(max, Number(this.quantityinfo.min || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.min))))
      const step = Math.trunc(Math.max(1, Number(this.quantityinfo.step || COMPONENT_PRODUCTBUYBUTTON_QUANTITY_DEFAULTS.step)))

      return {
        min,
        max,
        range: max - min,
        step
      }
    },
    quantitySteps() {
      const stepCount = Math.trunc(this.quantityOptions.range / this.quantityOptions.step) + 1

      return Array
        .from(Array(stepCount).keys())
        .map((step) => (step === 0 ? this.quantityOptions.min : this.quantityOptions.min + step * this.quantityOptions.step))
    },
    dropdownOptions() {
      const regularOptions = this.quantitySteps
        .slice(0, COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS)
        .map((value) => ({ value: String(value), text: String(value) }))

      const selectedValue = Number(this.quantity.value || this.quantityOptions.min)
      const selectedValueAsString = String(selectedValue)
      if (!regularOptions.some((option) => option.value === selectedValueAsString)) {
        regularOptions.unshift({ value: selectedValueAsString, text: selectedValueAsString })
      }

      if (this.showCustomQuantity && this.quantitySteps.length > COMPONENT_PRODUCTBUYBUTTON_DROPDOWN_MAX_ITEMS) {
        regularOptions.push({ value: CUSTOM_OPTION_VALUE, text: this.translations.quantity_more || 'mehr' })
      }

      return regularOptions
    },
    selectedDropdownValue() {
      return String(this.quantity.value || this.quantityOptions.min)
    }
  },
  methods: {
    setQuantityFromSelect(value) {
      if (value === CUSTOM_OPTION_VALUE) {
        this.toggleQuantityCustomMode(true)
        return
      }

      this.setQuantity(Number(value))
    },
    setQuantity(value = this.quantityOptions.min) {
      if (typeof value === 'number' && !Number.isNaN(value)) {
        this.quantity.value = value
        this.$emit('input', value)
      } else {
        this.toggleQuantityCustomMode(true)
      }
    },
    toggleQuantityCustomMode(state = false) {
      if (state) {
        this.quantity.tempValue = this.quantity.value
        this.quantity.value = null
        this.quantity.customMode = true

        this.$nextTick(() => {
          if (this.$refs.quantityControl) {
            this.$refs.quantityControl.focus()
          }
        })
      } else {
        const quantityValue = Number(this.quantity.value)
        const isNonNumeric = !/^-?([0-9.]){1,}$/.test(String(this.quantity.value))
        const isOutOfRange = quantityValue < this.quantityOptions.min || quantityValue > this.quantityOptions.max

        if (isNonNumeric || isOutOfRange) {
          this.quantity.value = this.quantity.tempValue || this.quantityOptions.min
        } else if (!this.quantitySteps.includes(quantityValue)) {
          this.quantity.value = closest(this.quantitySteps, quantityValue)
        } else {
          this.quantity.value = quantityValue
        }

        this.quantity.tempValue = null
        this.quantity.customMode = false
        this.$emit('input', Number(this.quantity.value))
      }
    }
  },
  watch: {
    value(nextValue) {
      if (!this.quantity.customMode) {
        this.quantity.value = Number(nextValue || this.quantityOptions.min)
      }
    }
  }
}
</script>

<style lang="scss">
.accessory-quantity-selector {
  width: 3.5rem;
  min-width: 3.5rem;
  max-width: 3.5rem;

  .custom-select {
    width: 3.5rem;
    min-width: 3.5rem;
    box-sizing: border-box;
    padding-left: 0.65rem;
    padding-right: 1.25rem;
    background-position: right 0.35rem center;
    text-overflow: clip;
  }

  .form-control {
    width: 3.5rem;
    min-width: 3.5rem;
    text-align: center;
    box-sizing: border-box;
  }
}
</style>
